import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import "./NavegacionSecciones.css";

const NavegacionSecciones = ({ secciones = [], className = "" }) => {
  const [activeId, setActiveId] = useState(() => {
    if (typeof window !== "undefined" && window.location.hash) {
      return window.location.hash.replace("#", "");
    }
    return secciones.length > 0 ? secciones[0].id : "";
  });

  const [isPinned, setIsPinned] = useState(false);

  const isClickScrollingRef = useRef(false);
  const clickTimeoutRef = useRef(null);
  const listRef = useRef(null);
  const chipRefs = useRef({});
  const sentinelRef = useRef(null);

  // Desplazamiento accesible respetando prefers-reduced-motion
  const scrollToTarget = useCallback((id, smooth = true) => {
    const element = document.getElementById(id);
    if (!element) return;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    element.scrollIntoView({
      behavior: smooth && !prefersReducedMotion ? "smooth" : "auto",
      block: "start",
    });
  }, []);

  // Manejador del click en cada botón/chip
  const handleItemClick = (e, id) => {
    e.preventDefault();
    setActiveId(id);

    // Evitar que el IntersectionObserver sobreescriba el id activo durante la animación de scroll
    isClickScrollingRef.current = true;
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    clickTimeoutRef.current = setTimeout(() => {
      isClickScrollingRef.current = false;
    }, 750);

    scrollToTarget(id, true);

    // Actualizar hash con replaceState para no saturar el historial de navegación hacia atrás
    if (typeof window !== "undefined" && window.history && window.history.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  // Mantener centrado el chip activo en pantallas móviles / con overflow horizontal
  useEffect(() => {
    if (!activeId) return;
    const activeChip = chipRefs.current[activeId];
    if (activeChip) {
      const prefersReducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      activeChip.scrollIntoView({
        inline: "center",
        block: "nearest",
        behavior: prefersReducedMotion ? "auto" : "smooth",
      });
    }
  }, [activeId]);

  // Carga inicial con hash en la URL (ej. /EstradosElectronicos#acuerdos-2026)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const initialHash = window.location.hash ? window.location.hash.replace("#", "") : "";
    if (initialHash) {
      setActiveId(initialHash);
      // Retardo breve para asegurar que las tablas de MaterialReactTable estén montadas en el DOM
      const timer = setTimeout(() => {
        scrollToTarget(initialHash, true);
      }, 450);
      return () => clearTimeout(timer);
    }
  }, [scrollToTarget]);

  // Detección del estado sticky a través de un sentinel
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof window === "undefined" || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsPinned(!entry.isIntersecting && entry.boundingClientRect.top <= 80);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: "-60px 0px 0px 0px",
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // IntersectionObserver para detectar la sección visible al hacer scroll
  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    if (secciones.length === 0) return;

    const sectionElements = secciones
      .map((item) => document.getElementById(item.id))
      .filter(Boolean);

    if (sectionElements.length === 0) return;

    // Margen superior negativo para compensar el header y la barra pegajosa (~120px)
    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrollingRef.current) return;

        // Filtrar las secciones visibles y elegir la que tenga mayor intersección
        const intersecting = entries.filter((entry) => entry.isIntersecting);
        if (intersecting.length > 0) {
          intersecting.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          const topVisible = intersecting[0];
          if (topVisible.target.id) {
            setActiveId(topVisible.target.id);
            if (window.history && window.history.replaceState) {
              window.history.replaceState(null, "", `#${topVisible.target.id}`);
            }
          }
        }
      },
      {
        root: null,
        rootMargin: "-120px 0px -55% 0px",
        threshold: [0, 0.2, 0.4, 0.6, 0.8, 1],
      }
    );

    sectionElements.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, [secciones]);

  if (!secciones || secciones.length < 2) return null;

  return (
    <>
      <div ref={sentinelRef} className="nav-secciones-sentinel" aria-hidden="true" />
      <div className={`nav-secciones-wrapper ${isPinned ? "is-pinned" : ""} ${className}`.trim()}>
        <nav aria-label="Secciones de la página" className="nav-secciones-container">
          <span className="nav-secciones-label" aria-hidden="true">
            Ir a la sección
          </span>
          <ul className="nav-secciones-list" ref={listRef}>
            {secciones.map((sec) => {
              const isActive = activeId === sec.id;
              const displayLabel = sec.label || sec.titulo || sec.title || "";
              const fullTitle = sec.titulo || sec.title || sec.label || "";
              return (
                <li key={sec.id} className="nav-secciones-item">
                  <a
                    href={`#${sec.id}`}
                    ref={(el) => (chipRefs.current[sec.id] = el)}
                    className={`nav-secciones-chip ${isActive ? "is-active" : ""}`}
                    aria-current={isActive ? "true" : undefined}
                    title={fullTitle}
                    aria-label={fullTitle}
                    onClick={(e) => handleItemClick(e, sec.id)}
                  >
                    {displayLabel}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </>
  );
};

NavegacionSecciones.propTypes = {
  secciones: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      titulo: PropTypes.string,
      title: PropTypes.string,
    })
  ).isRequired,
  className: PropTypes.string,
};

export default NavegacionSecciones;
